/// <reference types="@yandex-int/rum-counter/dist/inline/interface.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/inline/io.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/bundle/send.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/bundle/implementation.d.ts" />

import { isObject, JSONCompatible, Primitive, safeJsonStringify } from '@direct-frontend/stdlib';
import { createEvent } from '@direct-frontend/modules-core';

import { ElementTimingHero, ElementTimingObserver } from './element-timing';
import { buildRumId } from './buildRumId';
import { buildRumUrl } from './buildRumUrl';
import { getCLSAdditionalParams } from './attribution/getCLSAdditionalParams';
import { getINPAdditionalParams } from './attribution/getINPAdditionalParams';
import { getLCPAdditionalParams } from './attribution/getLCPAdditionalParams';
import { getLoAFAdditionalParams } from './attribution/getLoAFAdditionalParams';
import {
    createRequestWaterfallObserver,
    RequestWaterfallObserver,
    RequestWaterfallObserverOptions,
} from './requestWaterfallObserver';
import { createScrollObserver, ScrollObserver } from './scrollObserver';
import { subscribeToPageHide, subscribeToPageShow } from './pageVisibility';
import { RumError } from './RumError';
import { debugLog } from './debugLog';
import { LongAnimationFrameCollector, LongestAnimationFrameObserver } from './longest-animation-frame';
import { startCapturingInteractionTargets } from './captureInteractionTargets';

// BlockStat
// Подробнее: https://api.stat.yandex-team.ru/_api/dictionary/_blockstat/
const BlockStat = {
    ActionStartTime: '689.2322',
    SubPageIndex: '2925',
    SubPageName: '2924',
} as const;

// Флаг инициализации Rum счетчика.
let isRumInited: boolean = false;

// Глобальный индекс для подстраниц
// Используется для разметки подстраницы при SPA переходе
let globalSubPageIndex = 0;

// Инстанс ElementTimingObserver
let elementTimingObserver: ElementTimingObserver | null = null;
let unsubscribePageHide: null | (() => void) = null;
let unsubscribePageShow: null | (() => void) = null;

// Детектор долгих отрисовок (подвисаний интерфейса)
let longestAnimationFrameObserver: LongestAnimationFrameObserver | null = null;
let lastSentLoAFDuration = -1;
let loafCollector: LongAnimationFrameCollector | null = null;

// Детектор последовательных сетевых запросов
let requestWaterfallObserver: RequestWaterfallObserver | null = null;
let lastHWC = -1;

// Детектор событий скролла на странице
let scrollObserver: ScrollObserver | null = null;
let lastScrollJs = -1;
let lastScrollLatency = -1;

/** Остановить сбор target из событий интерактивности */
let stopCapturingInteractionTargets: null | (() => void) = null;

/** Событие готовности hero-элемента */
export let onHeroRenderEvent = createEvent();

/** Состояние готовности hero-элемента */
let isHeroRenderedFlag = false;

/**
 * Возвращает состояние готовности hero-элемента
 * @kind helper
 * @returns true, если hero-элемент отрендерился
 */
export function isHeroRendered() {
    return isHeroRenderedFlag;
}

/**
 * Хелпер, который позволяет определить доступен ли интерфейс Rum счетчика.
 *
 * @kind helper
 * @returns true, если интерфейс доступен, false — в обратном случае.
 */
function isRumAvailable() {
    return typeof window !== 'undefined' && Boolean(window.Ya?.Rum?.enabled);
}

/**
 * Получение инстанса Rum счетчика, если он был проинициализирован.
 *
 * @kind helper
 * @returns Инстанс Rum счетчика, если Rum недоступен или не проинициализирован — вернет undefined.
 */
function getRum() {
    if (!isRumAvailable() || !isRumInited) {
        return;
    }

    return window.Ya.Rum;
}

// Параметры для настройки Rum
interface RumOptions {
    requestWaterfallOptions?: RequestWaterfallObserverOptions;
}

/**
 * Инициализация бандловой части Rum счетчика.
 *
 * @kind helper
 * @param options — Параметры для настройки Rum
 * @returns undefined
 */
export function initRumBundle(options?: RumOptions) {
    if (isRumInited) {
        throw new RumError('RUM уже инициализирован', { cause: null });
    }

    if (!isRumAvailable()) {
        return;
    }

    require('@yandex-int/rum-counter/dist/bundle/implementation.min.js');
    require('@yandex-int/rum-counter/dist/bundle/onload.min.js');
    require('@yandex-int/rum-counter/dist/bundle/inp.min.js');

    isRumInited = true;

    const rum = getRum();
    if (rum) {
        onHeroRenderEvent.subscribe(() => {
            isHeroRenderedFlag = true;
        });

        stopCapturingInteractionTargets = startCapturingInteractionTargets();

        longestAnimationFrameObserver = new LongestAnimationFrameObserver();
        longestAnimationFrameObserver?.start(true);

        elementTimingObserver = new ElementTimingObserver(({ identifier, renderTimeFromLastNavigation }) => {
            rum.sendDelta(`element-timing-${identifier}`, renderTimeFromLastNavigation);

            if (identifier === ElementTimingHero) {
                onHeroRenderEvent.publish();
            }
        });
        elementTimingObserver.restart({ pageStartTime: 0, trackBuffered: true });

        requestWaterfallObserver = createRequestWaterfallObserver(options?.requestWaterfallOptions);
        requestWaterfallObserver.observe();

        scrollObserver = createScrollObserver();
        scrollObserver.observe();

        unsubscribePageHide = subscribeToPageHide(onPageHide);
        unsubscribePageShow = subscribeToPageShow(onPageShow);

        provideAttribution();
    }
}

/**
 * Сбрасывает состояние Rum счетчика.
 *
 * @unusedCodePublic Используется в тестах.
 * @kind helper
 * @returns undefined
 */
export function cleanupState() {
    elementTimingObserver?.stop();
    elementTimingObserver = null;

    longestAnimationFrameObserver?.dispose();
    longestAnimationFrameObserver = null;
    lastSentLoAFDuration = -1;

    requestWaterfallObserver?.disconnect();
    requestWaterfallObserver = null;
    lastHWC = -1;

    stopCapturingInteractionTargets?.();

    scrollObserver?.disconnect();
    scrollObserver = null;
    lastScrollJs = -1;
    lastScrollLatency = -1;
    onHeroRenderEvent = createEvent();
    isHeroRenderedFlag = false;

    unsubscribePageHide?.();
    unsubscribePageShow?.();

    globalSubPageIndex = 0;

    isRumInited = false;
}

/**
 * Отправить счетчик с дельтой времени.
 *
 * @kind helper
 * @param name - ID метки времени.
 * @param delta - Точное значение дельты.
 * @param additional - Дополнительные данные. Должны быть простым key-value объектом.
 * @returns undefined
 */
export function sendDelta(name: string, delta: number, additional?: Record<string, Primitive>) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    let params: Ya.Rum.RumParams | undefined = undefined;
    if (additional) {
        params = {
            '-additional': encodeURIComponent(makeAdditional(additional)),
        };
    }

    debugLog(() => ({
        tags: ['Delta', name, delta.toFixed(2)],
        details: {
            additional,
        },
    }));
    rum.sendDelta(name, delta, params);
}

/**
 * Отправить счетчик с временной меткой.
 *
 * @kind helper
 * @param name - ID метки времени.
 * @param time - Время. Если не передано, вычисляется время от начала навигации до момента вызова функции.
 * @returns undefined
 */
export function sendTimeMark(name: string, time?: number) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({
        tags: ['Time mark', name, time?.toFixed(2)],
    }));
    rum.sendTimeMark(name, time);
}

/**
 * Получение '-additional' параметра Rum счетчика.
 *
 * @kind helper
 * @returns Значение параметра additional в распаршенном виде.
 */
export function getAdditional() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    const rawAdditional = rum._vars['-additional'];
    if (!rawAdditional) {
        return;
    }

    try {
        const parsedAdditional = JSON.parse(rawAdditional);

        if (isObject(parsedAdditional)) {
            return parsedAdditional;
        }

        throw new RumError('Содержимое поля additional в RUM не является объектом', { cause: null });
    } catch (error) {
        throw new RumError('Ошибка парсинга additional в RUM', { cause: error });
    }
}

/**
 * Отложенная установка имени просматриваемой страницы.
 *
 * @kind helper
 * @param pageName — Имя просматриваемой страницы.
 * @returns undefined
 */
export function setDeferredPageName(pageName: string) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({ tags: ['Set page name', pageName] }));

    rum.setDeferredPageName(pageName);
}

/**
 * Регистрация SubPage'а при SPA переходе.
 *
 * @kind helper
 * @returns undefined
 */
export function registerSubPage() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({ tags: ['Register SubPage'] }));

    // Сохраняем время начала регистрации подстраницы.
    // Это время пригодится нам для отправки TTI и для сохранения информации в Rum счетчике.
    const registerSubPageStartTime = rum.getTime();

    // Отправляем все накопленные данные в Rum.
    sendHighestWaterfallCount();
    sendScrollJsMetric();
    sendScrollLatencyMetric();

    // Посылаем самый длинный LoAF для текущей страницы
    sendLongestAnimationFrame();
    resetLongestAnimationFrame();

    // Сбрасываем все метрики для новых замеров на новой странице.
    resetHighestWaterfallCount();
    resetScrollMetrics();

    // Завершаем сессию.
    rum.completeSession(true);

    // Сбрасываем имя текущей страницы, чтобы новые метрики отправлялись уже для новой.
    rum.resetDeferredPageName();

    // Триггерим отправку TTI в Rum для новой страницы, отсчет берем от начала регистрации подстраницы.
    rum.sendTTI(undefined, registerSubPageStartTime);

    // Очищаем собранные LoAF.
    // INP сбрасывается после completeSession, значит хранить их нет смысла
    loafCollector?.clear();

    // Перезапускаем ElementTimingObserver, указываем точку отсчета новой страницы.
    elementTimingObserver?.restart({ pageStartTime: performance.now() });
    isHeroRenderedFlag = false;

    // Собираем page_id из параметров счетчика
    const project = rum._vars['-project'] ?? 'unknown';
    const service = rum._vars['-service'];
    const platform = rum._vars['-platform'] ?? 'unknown';
    const subPageId = buildRumId({ project, service, platform });

    // Формируем subPageIndex и actionStartTime для нового subpage'а
    const subPageIndex = String(globalSubPageIndex++);
    const actionStartTime = String(registerSubPageStartTime);

    // Добавляем данные о subpage'е в additional для реалтаймовых метрик
    const additional = makeAdditional({
        subpage_name: subPageId,
        subpage_index: subPageIndex,
        action_start_time: actionStartTime,
    });

    // При обновлении параметров важно сохранить ID из BlockStat'а,
    // т.к. на них завязан код Тренчера.
    rum.setVars({
        rum_id: subPageId,
        '-page': subPageId,
        '-additional': additional,
        [BlockStat.SubPageName]: subPageId,
        [BlockStat.SubPageIndex]: subPageIndex,
        [BlockStat.ActionStartTime]: actionStartTime,
    });
}

/**
 * Регистрация текущего URL страницы в параметрах RUM счетчика.
 *
 * @kind helper
 */
export function registerUrl() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    const url = buildRumUrl();

    debugLog(() => ({ tags: ['Register URL'], details: { url } }));

    // Докидываем URL в '-additional', не используя поле '-url',
    // потому что поле '-url' не прорастает в ClickHouse таблицы.
    rum.setVars({ '-additional': makeAdditional({ url }) });
}

/**
 * Отправляет метрику с информацией о самом длинном кадре
 */
function sendLongestAnimationFrame() {
    const rum = getRum();
    if (!rum || !longestAnimationFrameObserver) {
        return;
    }

    const longestAnimationFrame = longestAnimationFrameObserver.getLongestAnimationFrame();
    if (!longestAnimationFrame || lastSentLoAFDuration >= longestAnimationFrame.duration) {
        return;
    }

    sendDelta('loaf', longestAnimationFrame.duration, getLoAFAdditionalParams(longestAnimationFrame));
    lastSentLoAFDuration = longestAnimationFrame.duration;
}

/**
 * Логирует максимальный waterfall запросов.
 */
function sendHighestWaterfallCount() {
    const rum = getRum();
    if (!rum || !requestWaterfallObserver?.supported()) {
        return;
    }

    const waterfall = requestWaterfallObserver.getHighestWaterfall();
    const hwc = Math.max(waterfall.requestsCount - 1, 0);

    // Не логируем повторно одно и то же значение
    if (lastHWC === hwc) {
        return;
    }

    const attribution = hwc > 0 ? waterfall.attribution : [];
    const names = attribution.map(({ name }) => encodeURIComponent(name)).join(',');
    const durations = attribution.map(({ duration }) => duration).join(',');

    debugLog(() => ({
        tags: ['HWC', hwc.toString()],
        details: attribution,
    }));
    sendDelta('hwc', hwc, {
        hwc_waterfall: names,
        hwc_duration: durations,
        hwc_total_duration: waterfall.totalDuration,
    });

    lastHWC = hwc;
}

/**
 * Cбрасывает метрику максимального waterfall запросов.
 */
function resetHighestWaterfallCount() {
    lastHWC = -1;
    requestWaterfallObserver?.reset();
}

function resetLongestAnimationFrame() {
    lastSentLoAFDuration = -1;
    longestAnimationFrameObserver?.resetLongestAnimationFrame();
}

/**
 * Логирует максимальную задержку обработки JS при скролле.
 */
function sendScrollJsMetric() {
    const rum = getRum();
    if (!rum || !scrollObserver) {
        return;
    }

    const scrollJsMetric = scrollObserver.getScrollJsMetric();
    if (!scrollJsMetric) {
        return;
    }

    const { time, attribution } = scrollJsMetric;

    // Не логируем повторно одно и то же значение
    if (time === lastScrollJs) {
        return;
    }

    debugLog(() => ({ tags: ['Scroll JS', String(time)], details: attribution }));
    // Добавляем в additional поле scroll_js_hrtime с метрикой, чтобы строить более точные графики.
    // Потому что в таблице rum.delta метрика хранится, как целое число.
    sendDelta('scroll_js', time, {
        scroll_js_hrtime: time,
        scroll_js_selector: attribution.selector,
    });

    lastScrollJs = time;
}

/**
 * Логирует максимальную задержку отрисовки при скролле.
 */
function sendScrollLatencyMetric() {
    const rum = getRum();
    if (!rum || !scrollObserver) {
        return;
    }

    const scrollLatencyMetric = scrollObserver.getScrollLatencyMetric();
    if (!scrollLatencyMetric) {
        return;
    }

    const { time, attribution } = scrollLatencyMetric;

    // Не логируем повторно одно и то же значение
    if (time === lastScrollLatency) {
        return;
    }

    debugLog(() => ({ tags: ['Scroll Latency', String(time)], details: attribution }));
    // Добавляем в additional поле scroll_latency_hrtime с метрикой, чтобы строить более точные графики.
    // Потому что в таблице rum.delta метрика хранится, как целое число.
    sendDelta('scroll_latency', time, {
        scroll_latency_hrtime: time,
        scroll_latency_selector: attribution.selector,
    });

    lastScrollLatency = time;
}

/**
 * Сбрасывает метрики скролла.
 */
function resetScrollMetrics() {
    lastScrollJs = -1;
    lastScrollLatency = -1;
    scrollObserver?.reset();
}

/**
 * Установка параметров в Rum счетчике для сборка доп. информации об LCP, CLS, INP.
 *
 * @kind helper
 * @returns undefined
 */
function provideAttribution() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    // Достаточно, чтобы найти LoAF, которые могут быть связаны с INP
    const bufferSize = 50;
    loafCollector = new LongAnimationFrameCollector(bufferSize);
    loafCollector.start(true);

    rum.getCLSAdditionalParams = createAttributionProvider(getCLSAdditionalParams);
    rum.getINPAdditionalParams = createAttributionProvider(performanceEventTiming => {
        return getINPAdditionalParams(
            performanceEventTiming,
            loafCollector?.getRelatedLoAFs(performanceEventTiming.startTime, performanceEventTiming.processingEnd)
        );
    });
    rum.getLCPAdditionalParams = createAttributionProvider(getLCPAdditionalParams);
}

/**
 * Коллбэк, который вызовется на скрытие/закрытие страницы.
 */
function onPageHide() {
    debugLog(() => ({ tags: ['Page hide'] }));
    // Останавливаем сбор метрики, так как метрики в таком случае не валидные:
    // вкладка в фоне работает медленнее (inactive tab throttling), а beforeunload блокирует основной поток.
    elementTimingObserver?.stop();

    sendLongestAnimationFrame();
    longestAnimationFrameObserver?.stop();
    loafCollector?.stop();

    sendHighestWaterfallCount();
    sendScrollJsMetric();
    sendScrollLatencyMetric();
}

/**
 * Коллбэк, который вызовется на показ страницы после скрытия.
 */
function onPageShow() {
    debugLog(() => ({ tags: ['Page show'] }));
    longestAnimationFrameObserver?.start();
    loafCollector?.start();
}

/**
 * Создает новый сериализованный объект additional на основе текущего значения в Rum счетчике.
 *
 * @param obj Параметры, которые нужно добавить в addditional.
 * @returns Сериализованый объект obj, смерженный с текущим значением additional
 */
function makeAdditional<T extends JSONCompatible<T>>(obj: T) {
    const additional = getAdditional() ?? {};
    Object.assign(additional, obj);
    return safeJsonStringify(additional);
}

/**
 * Создает провайдер дополнительных параметров для метрик,
 * правильно добавляя все значения в additional.
 *
 * @param getAttribution Метод для получения доп. данных по логируемой метрике.
 * @returns Метод для сбора доп. данных по метрике, с правильным формированием параметров для RUM.
 */
function createAttributionProvider<T>(getAttribution: (event: T) => Record<string, string> | undefined) {
    return (event: T) => {
        const attribution = getAttribution(event);

        return {
            '-additional': encodeURIComponent(makeAdditional(attribution)),
        };
    };
}
